import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { ActivityComment as ActivityCommentType } from 'src/models/activity/_types';

import { DropdownConfirm } from '../DropdownConfirm';
import { TimeAgo } from '../TimeAgo';

interface ActivityCommentProps {
  comment: ActivityCommentType;
  canDelete?: boolean;
  onDelete?: () => void;
}

export const ActivityComment = ({
  comment,
  canDelete,
  onDelete,
}: ActivityCommentProps) => {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const { t } = useTranslation('translation', {
    keyPrefix: 'activity.comments',
  });
  const [confirmProps] = useConfirmDialogComponent();

  const handleDelete = async () => {
    onDelete?.();
  };
  return (
    <Box p={2}>
      <Stack direction="row" spacing={2}>
        <Avatar src={comment.user.image} />
        <Box flex={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2" gutterBottom>
              {comment.user.nickname}
              <Typography
                variant="body2"
                color="text.secondary"
                component={'span'}
                ml={2}
              >
                <TimeAgo timestamp={comment.createdAt} />
              </Typography>
            </Typography>
            {canDelete && (
              <DropdownConfirm
                title={tCommon('confirm')}
                message={t('deleteConfirm')}
                onConfirm={() => handleDelete()}
                trigger={
                  <IconButton size="small" aria-label="Delete Comment">
                    <Icon icon={faTrash} />
                  </IconButton>
                }
              />
            )}
          </Stack>
          <HtmlTypography
            color="text.secondary"
            linkifyOptions={{ nl2br: false }}
            sx={{
              '& a.mention': {
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
          >
            {comment.content}
          </HtmlTypography>
        </Box>
      </Stack>
      <ConfirmDialog {...confirmProps} />
    </Box>
  );
};
